import { useState, KeyboardEvent, useEffect } from 'react';

import { useParams } from 'react-router-dom';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Box,
  SimpleGrid,
  Text,
  useColorModeValue,
  Heading,
  Flex,
  Input,
  InputGroup,
  Button,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Stack,
  Select,
} from '@chakra-ui/react';

import { SearchIcon, SmallCloseIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';
import Paginate from 'components/paginate/Paginate';
import SelectSearch from 'components/fields/SelectSearch';

import { apiGetUsers, apiAddUserToWorkflow, apiGetBranches } from 'api';

import type {
  APIPaginatedResponse,
  APIUserListResultData,
  APIBranchData,
} from 'types/api.types';

import { ROLES_OPTIONS } from 'variables/options';

import AddUserButtons from './AddUserButtons';
import UserCard from './UserCard';

import type { WorkflowUsersProps } from './interfaces';

const WorkflowUsers = ({
  users,
  isRefetchingUsers,
  handleRefetch,
}: WorkflowUsersProps) => {
  const { id: workflowId } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');

  // Search states
  const [nameSearch, setNameSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedIsActive, setSelectedIsActive] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchPage, setSearchPage] = useState(0);

  // Query to fetch branches
  const { data: branchesData = [], isLoading: isLoadingBranches } = useQuery({
    queryKey: ['branches-all'],
    queryFn: () => apiGetBranches(null, 'all'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  }) as {
    data: APIBranchData[];
    isLoading: boolean;
    error: any;
  };

  // Query for searching users
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['workflow-user-search', searchQuery, searchPage],
    queryFn: () => apiGetUsers(searchPage + 1, searchQuery),
    enabled: !!searchQuery,
  }) as {
    data: APIPaginatedResponse<APIUserListResultData>;
    isLoading: boolean;
    error: any;
  };

  // Reset search page when query changes
  useEffect(() => {
    if (searchQuery) {
      setSearchPage(0);
    }
  }, [searchQuery]);

  // Add user to workflow mutation
  const addUserMutation = useMutation({
    mutationFn: ({
      userId,
      hasNotifications,
    }: {
      userId: number;
      hasNotifications: boolean;
    }) => {
      return apiAddUserToWorkflow(parseInt(workflowId), userId, {
        has_new_lead_email: hasNotifications,
      });
    },
    onSuccess: () => {
      toast({
        title: 'User added to workflow',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // Refresh the user list
      handleRefetch();
    },
    onError: (error) => {
      toast({
        title: 'Failed to add user',
        description: error.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  // Handle user search
  const handleSearch = () => {
    const values = {
      name: nameSearch,
      email: emailSearch,
      role: selectedRole,
      branch: selectedBranch,
      is_active: selectedIsActive,
    };

    const query = Object.entries(values)
      .filter(([_, value]) => value.length > 0)
      .map(([key, value]) => `${key}=${encodeURIComponent(value.trim())}`)
      .join('&');

    if (query !== searchQuery) {
      setSearchQuery(query);
    }
  };

  const resetSearch = () => {
    setNameSearch('');
    setEmailSearch('');
    setSelectedRole('');
    setSelectedBranch('');
    setSelectedIsActive('');
    setSearchQuery('');

    setSearchPage(0);
    queryClient.invalidateQueries({
      queryKey: ['workflow-user-search'],
    });
    handleRefetch();
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle adding a user to the workflow
  const handleAddUser = (
    user: APIUserListResultData,
    withNotifications: boolean,
  ) => {
    addUserMutation.mutate({
      userId: user.user.id,
      hasNotifications: withNotifications,
    });
  };

  // Check if a user is already in the workflow
  const isUserInWorkflow = (userId: number): boolean => {
    return users.some((workflowUser) => workflowUser.user.id === userId);
  };

  return (
    <>
      <Card mb="20px">
        <Heading size="md" mb={6} color={textColor}>
          Add users to workflow
        </Heading>

        <Stack
          spacing={4}
          direction={{ base: 'column', lg: 'row' }}
          align="flex-end"
        >
          <Box flex="1">
            <InputGroup>
              <Input
                variant="main"
                placeholder="Name"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                bg={inputBg}
                size="md"
                onKeyDown={handleKeyDown}
              />
            </InputGroup>
          </Box>

          <Box flex="1">
            <InputGroup>
              <Input
                variant="main"
                placeholder="Email"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                bg={inputBg}
                size="md"
                onKeyDown={handleKeyDown}
              />
            </InputGroup>
          </Box>

          <Box flex="1">
            <Select
              variant="main"
              placeholder="All roles"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              bg={inputBg}
              size="md"
              onKeyDown={handleKeyDown}
            >
              {ROLES_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </Select>
          </Box>

          <Box flex="1">
            <SelectSearch
              options={branchesData.map((branch) => ({
                value: branch.id.toString(),
                label: branch.name,
              }))}
              value={selectedBranch}
              onChange={(value) => setSelectedBranch(value?.toString() || '')}
              placeholder="Select Branch"
              isDisabled={isLoadingBranches}
              allowClear
            />
          </Box>

          <Box flex="1">
            <Select
              variant="main"
              placeholder="Status"
              value={selectedIsActive}
              onChange={(e) => setSelectedIsActive(e.target.value)}
              bg={inputBg}
              size="md"
              onKeyDown={handleKeyDown}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Select>
          </Box>

          <Flex gap={3}>
            <Button
              onClick={handleSearch}
              leftIcon={<SearchIcon />}
              size="md"
              variant="brand"
              isLoading={isSearching}
            >
              Search
            </Button>
            <Button
              onClick={resetSearch}
              variant="outline"
              leftIcon={<SmallCloseIcon />}
              size="md"
            >
              Reset
            </Button>
          </Flex>
        </Stack>

        {isSearching ? (
          <Flex justify="center" align="center" mt={4}>
            <Spinner size="sm" mr={2} /> Searching users...
          </Flex>
        ) : (
          searchResults && (
            <Box borderTop="1px solid" borderColor={borderColor} mt={4} pt={2}>
              {searchResults.results.length === 0 ? (
                <Box mt={4} textAlign="center">
                  No users found matching your search.
                </Box>
              ) : (
                <>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th borderColor={borderColor} pb={3}>
                          First Name
                        </Th>
                        <Th borderColor={borderColor} pb={3}>
                          Lsat Name
                        </Th>
                        <Th borderColor={borderColor} pb={3}>
                          Email
                        </Th>
                        <Th borderColor={borderColor} pb={3}>
                          Role
                        </Th>
                        <Th borderColor={borderColor} pb={3}>
                          Actions
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {searchResults.results.map((user) => {
                        const alreadyInWorkflow = isUserInWorkflow(
                          user.user.id,
                        );

                        return (
                          <Tr key={user.user.id}>
                            <Td>
                              <Text color={textColor} fontSize="sm">
                                {user.user.first_name}
                              </Text>
                            </Td>
                            <Td>
                              <Text color={textColor} fontSize="sm">
                                {user.user.last_name}
                              </Text>
                            </Td>
                            <Td>
                              <Text color={textColor} fontSize="sm">
                                {user.user.email}
                              </Text>
                            </Td>
                            <Td>
                              <Text color={textColor} fontSize="sm">
                                {user.role_display}
                              </Text>
                            </Td>
                            <Td w="40%">
                              <AddUserButtons
                                user={user}
                                onAdd={handleAddUser}
                                hide={alreadyInWorkflow}
                              />
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>

                  <Paginate
                    page={searchPage}
                    count={searchResults.count}
                    onSetPage={(newPage) => {
                      setSearchPage(newPage);
                    }}
                  />
                </>
              )}
            </Box>
          )
        )}
      </Card>

      <Card mb="20px">
        <Heading size="md" mb={4} color={textColor}>
          Workflow Users {isRefetchingUsers && <Spinner size="sm" ml={2} />}
        </Heading>

        {users.length === 0 ? (
          <Box textAlign="center" mb={4}>
            No users assigned to this workflow.
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {users.map((workflowUser) => (
              <UserCard users={users} user={workflowUser} />
            ))}
          </SimpleGrid>
        )}
      </Card>
    </>
  );
};

export default WorkflowUsers;
